(ns manatee.domain.task.spec
  (:require [clojure.set :as set]
            [clojure.spec.alpha :as s]
            [clojure.string :as str]
            [manatee.domain.edit :as edit]
            [manatee.domain.external :as external]
            [rothko.editor.document.script.spec :as script-spec]
            [spec-tools.data-spec :as ds]))

(def base-edit-spec
  {:status (s/spec #{:done :rejected})
   :item-id :manatee.domain.item/id
   :item-type :manatee.domain.item/item-type
   :account-id ::external/account-id
   (ds/opt :evaluator-edit?) boolean?})

(defmulti field-spec (fn [_k field] (:field-type field)))

(defmethod field-spec :drop-down
  [k {:keys [options]}]
  (let [values (set (map :value options))]
    {k (s/spec values)}))

(defmethod field-spec :scoring
  [_ {:keys [criteria] :as field}]
  (reduce (fn [spec {:keys [value optional] :as criterion}]
            (let [criteria-name (keyword value)
                  criteria-spec-name (if (contains? #{:language-resource :task-achievement :register} criteria-name)
                                       (ds/opt criteria-name)
                                       criteria-name)
                  score-range (:score-range criterion
                                            (:score-range field))]
              (assoc spec criteria-spec-name (s/spec (if optional
                                                       (s/nilable (set score-range))
                                                       (set score-range))))))
          {}
          criteria))

(defmethod field-spec :labelled-scoring
  [_k {:keys [criteria] :as _field}]
  (reduce (fn [spec {:keys [value options] :as _criterion}]
            (assoc spec
                   (keyword value) (s/spec (set (map :value options)))))
          {}
          criteria))

(defmethod field-spec :string
  [k & _]
  {k string?})

(doseq [field-type [:transcript-annotation :error-annotation]]
  (defmethod field-spec field-type
    [k & _]
    {k ::script-spec/transcript}))

(defmethod field-spec :script-annotation
  [k & _]
  {k ::script-spec/script})

(defmethod field-spec :mwe-annotation
  [k & _]
  {k ::script-spec/mwe-script})

(defmethod field-spec :segment-transcript-annotation
  [k & _]
  {k ::edit/segments})

(defmethod field-spec :time-markers
  [k & _]
  {k ::edit/time-markers})

(defn add-field-spec
  "Generates a data-spec map for a field based on `:field-type`,
  then adds it into the accumulating data-spec map."
  [spec {:keys [field-name _field-type optional?] :as field}]
  (let [k (if optional?
            (ds/opt field-name)
            field-name)
        field-spec (field-spec k field)]
    (merge spec field-spec)))

(defn fields-spec
  [fields]
  (reduce add-field-spec
          {}
          fields))

(def rejected-statuses #{:rejected :rejected-requires-approval})

(defn override-statuses
  [allowed-statuses]
  (when (seq allowed-statuses)
    {:status (s/spec (->> allowed-statuses
                          (map keyword)
                          set
                          (#(set/difference % rejected-statuses))))}))

(defn annotated-spec
  [{:keys [fields allowed-statuses]} item-type]
  (merge base-edit-spec
         {:item-type (s/spec #{item-type})}
         (override-statuses allowed-statuses)
         (fields-spec fields)))

(defn rejected-spec
  [{{:keys [reasons]} :rejections
    :keys             [fields allowed-statuses]
    :as               _section}
   item-type
   {:keys [allowed-auto-rejections reject-with-annotation?]
    :as   _modifiers}]
  (let [rs   (->> reasons

                  ; Items conforming to the generated spec. can contain a
                  ; rejection reason drawn from the union of the task
                  ; configuration rejection reasons and the auto-rejection
                  ; reasons.
                  ; As the auto-rejection reasons are no longer contained in
                  ; the task configuration rejection reasons (see
                  ; https://btsservice.atlassian.net/browse/ELIT-3716), they
                  ; require adding here.
                  (concat allowed-auto-rejections)

                  (map :value)
                  (map keyword)
                  (set))
        rejections (s/coll-of rs :kind vector? :distinct true :min-count 1)
        base (cond-> {:item-type (s/spec #{item-type})
                      :status    (s/spec (set/intersection (set (map keyword allowed-statuses))
                                                           rejected-statuses))
                      :reason    (s/with-gen (s/or :old (s/spec rs)
                                                   :new rejections)
                                   #(s/gen rejections))}

               reject-with-annotation?
               (merge (fields-spec fields)))]
    (ds/or
     {:rejected-standard base
      :rejected-other    (merge base
                                {:other-reason (s/and string? not-empty)})})))

(defn spec-name
  [{:keys [task-name id] :as task-config} status-category item-type]
  (keyword "manatee.domain.task.spec"
           (str/join "-" [task-name
                          ;; Specs from different (but named the same) task-configs should have different names, otherwise only the last wins.
                          (or id (hash task-config))
                          status-category
                          (name item-type)])))

(defmulti custom-validation-rule (fn [rule-name _edit] rule-name))

(defmethod custom-validation-rule :reject-only-when-content-scored-0
  [_ {:keys [item-type status content] :as _edit}]
  (when (and (= item-type :answer)
             (not= (contains? #{:rejected :rejected-requires-approval} status)
                   (= content 0)))
    "You can only reject when Content is scored 0 and submit when Content is scored 1."))

(defn run-custom-validation
  ([edit task-config]
   (run-custom-validation nil edit task-config))
  ([spec edit {{:keys [custom-validation-rules]} :data}]
   (when-let [rule-name (first custom-validation-rules)]
     (assert (not (second custom-validation-rules)) "Only one rule per task is currently supported.")
     (custom-validation-rule rule-name (cond->> edit spec (s/unform spec))))))

(defn spec-with-custom-validation
  [spec task-config]
  (s/and spec (fn [edit] (nil? (run-custom-validation spec edit task-config)))))

(defn task-config->edit-spec
  "Generate an item-edit spec based on a task_config table entry."
  [{{:keys [part answer modifiers]} :data :as task-config} {:keys [item-type status] :as _edit}]
  (let [section (case item-type :part part :answer answer ::unknown)
        rejected? (contains? rejected-statuses status)]
    (-> (cond
          (= section ::unknown) (ds/spec
                                 {:name (spec-name task-config "unknown" "unknown")
                                  :spec {:item-type :manatee.domain.item/item-type}})
          rejected? (ds/spec
                     {:name (spec-name task-config "rejected" item-type)
                      :spec (rejected-spec section item-type modifiers)})
          :else (ds/spec
                 {:name (spec-name task-config "annotated" item-type)
                  :spec (annotated-spec section item-type)}))
        (spec-with-custom-validation task-config))))
